import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Play, Youtube, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface DemoVideoSectionProps {
  videoUrl?: string;
  videoType?: 'youtube' | 'loom' | 'vimeo';
}

export function DemoVideoSection({ 
  videoUrl,
  videoType = 'youtube' 
}: DemoVideoSectionProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  const getEmbedUrl = () => {
    if (!videoUrl) return null;
    
    if (videoType === 'youtube') {
      const videoId = videoUrl.includes('youtu.be') 
        ? videoUrl.split('/').pop()
        : new URLSearchParams(new URL(videoUrl).search).get('v');
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
    
    if (videoType === 'loom') {
      const videoId = videoUrl.split('/').pop();
      return `https://www.loom.com/embed/${videoId}?autoplay=1`;
    }
    
    if (videoType === 'vimeo') {
      const videoId = videoUrl.split('/').pop();
      return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    }
    
    return videoUrl;
  };

  const embedUrl = getEmbedUrl();

  return (
    <section id="demo" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold md:text-4xl">
            See Vewly in Action
          </h2>
          <p className="text-lg text-muted-foreground">
            Watch how easy it is to set up and start streaming in under 60 seconds
          </p>
        </div>

        <Card className="max-w-4xl mx-auto overflow-hidden shadow-lg">
          <CardContent className="p-0">
            <div className="relative aspect-video bg-gradient-to-br from-primary/10 to-primary/5">
              {embedUrl && isPlaying ? (
                <iframe
                  className="w-full h-full"
                  src={embedUrl}
                  title="Vewly Demo Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="rounded-full bg-primary/20 p-8 mb-4 hover:bg-primary/30 transition-colors cursor-pointer" onClick={() => setIsPlaying(true)}>
                    <Play className="h-16 w-16 text-primary" />
                  </div>
                  <p className="text-lg font-semibold mb-2">
                    {embedUrl ? "Watch Demo Video" : "Demo Video Coming Soon"}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    60-second walkthrough of Vewly&apos;s key features
                  </p>
                  {embedUrl && (
                    <Button 
                      onClick={() => setIsPlaying(true)}
                      size="lg"
                      className="gap-2"
                    >
                      <Play className="h-5 w-5" />
                      Play Video
                    </Button>
                  )}
                  {!embedUrl && (
                    <Button 
                      variant="outline"
                      size="lg"
                      className="gap-2"
                      asChild
                    >
                      <a 
                        href="https://www.youtube.com/watch?v=dQw4w9WgXcQ" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Youtube className="h-5 w-5" />
                        View on YouTube
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Video Highlights Timeline */}
        <div className="mt-8 max-w-4xl mx-auto grid gap-4 sm:grid-cols-2 md:grid-cols-4 text-center">
          <div className="p-4 rounded-lg bg-background/50 border hover:border-primary transition-colors">
            <div className="text-2xl font-bold text-primary mb-1">0:00-0:15</div>
            <p className="text-sm font-medium mb-1">One-Tap Setup</p>
            <p className="text-xs text-muted-foreground">Quick installation wizard</p>
          </div>
          <div className="p-4 rounded-lg bg-background/50 border hover:border-primary transition-colors">
            <div className="text-2xl font-bold text-primary mb-1">0:15-0:30</div>
            <p className="text-sm font-medium mb-1">AI Discovery</p>
            <p className="text-xs text-muted-foreground">Smart recommendations</p>
          </div>
          <div className="p-4 rounded-lg bg-background/50 border hover:border-primary transition-colors">
            <div className="text-2xl font-bold text-primary mb-1">0:30-0:45</div>
            <p className="text-sm font-medium mb-1">4K Streaming (Pro Pass)</p>
            <p className="text-xs text-muted-foreground">Ultra HD quality playback</p>
          </div>
          <div className="p-4 rounded-lg bg-background/50 border hover:border-primary transition-colors">
            <div className="text-2xl font-bold text-primary mb-1">0:45-1:00</div>
            <p className="text-sm font-medium mb-1">Plugin Marketplace</p>
            <p className="text-xs text-muted-foreground">Extend functionality</p>
          </div>
        </div>
      </div>
    </section>
  );
}
